import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { callApi, HttpMethod } from "src/services/apiService";
import { toast } from "sonner";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

// --- Interfaces ---

interface DeviceCounts {
  total: number;
  active: number;
  inactive: number;
  warmupReserved: number;
  virtual: number;
  handset: number;
}

interface CarrierSummary {
  carrier: string;
  total: number;
  active: number;
  warmupReserved: number;
  queueDepth: number;
  highPriorityQueueDepth: number;
  callsInLastHour: number;
  callsInLast24Hours: number;
  connectedInLastHour: number;
  connectedInLast24Hours: number;
}

interface AlertDevice {
  id: number;
  phoneNumber: string;
  carrier: string;
  location: string;
  lastCalled: string | null;
  lastPlacedCallToDevice: string | null;
  missedCalls: number | null;
  blackScreenshotsInLastHour: number | null;
  deviceType: string | null;
  totalCalls: number | null;
  flaggedAsSpam: number | null;
  flaggedAsClean: number | null;
  spamPercentage: number | null;
  callLogScreenshots: number | null;
}

interface HealthAlert {
  type: string;
  severity: string;
  title: string;
  description: string;
  count: number;
  devices: AlertDevice[];
}

interface QueueStats {
  totalMessages: number;
  totalHighPriority: number;
}

interface SimfarmDashboardData {
  deviceCounts: DeviceCounts;
  carrierSummaries: CarrierSummary[];
  healthAlerts: HealthAlert[];
  queueStats: QueueStats;
}

// --- Helpers ---

const formatPhone = (phone: string): string => {
  if (!phone || phone.length !== 10) return phone;
  return `${phone.slice(0, 3)} ${phone.slice(3, 6)} ${phone.slice(6)}`;
};

const formatDate = (d: string | null): string => {
  if (!d) return "--";
  try {
    return new Date(d).toLocaleString();
  } catch {
    return d;
  }
};

const severityBorderClass = (severity: string): string => {
  switch (severity.toLowerCase()) {
    case "danger":
    case "critical":
      return "border-danger";
    case "warning":
      return "border-warning";
    default:
      return "border-secondary";
  }
};

const severityTextClass = (severity: string): string => {
  switch (severity.toLowerCase()) {
    case "danger":
    case "critical":
      return "text-danger";
    case "warning":
      return "text-warning";
    default:
      return "text-secondary";
  }
};

const CARRIER_COLORS: Record<string, string> = {
  Verizon: "#cd040b",
  ATT: "#009fdb",
  TMobile: "#e20074",
};

// --- Component ---

export default function SimFarmDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState<SimfarmDashboardData | null>(null);
  const [deepAlerts, setDeepAlerts] = useState<HealthAlert[]>([]);
  const [deepLoading, setDeepLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const fetchDashboard = useCallback(async () => {
    try {
      const result: SimfarmDashboardData = await callApi(
        "sysadmin/simfarm/dashboard",
        HttpMethod.GET,
      );
      setData(result);
      setLastUpdated(new Date());
    } catch (err: any) {
      toast.error(err?.message || "Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDeepChecks = useCallback(async () => {
    setDeepLoading(true);
    try {
      const result: HealthAlert[] = await callApi(
        "sysadmin/simfarm/dashboard/deep-checks",
        HttpMethod.GET,
      );
      setDeepAlerts(result);
    } catch {
      // Deep checks are optional — don't show error toast
    } finally {
      setDeepLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
    fetchDeepChecks();
    const interval = setInterval(fetchDashboard, 60_000);
    return () => clearInterval(interval);
  }, [fetchDashboard, fetchDeepChecks]);



  if (loading && !data) {
    return (
      <div className="container-fluid py-4 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const queueChartData = data.carrierSummaries.map((c) => ({
    carrier: c.carrier,
    "Queue Depth": c.queueDepth,
    "High Priority": c.highPriorityQueueDepth,
  }));

  return (
    <div className="container-fluid py-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">SIM Farm Dashboard</h2>
          {lastUpdated && (
            <small className="text-muted">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </small>
          )}
        </div>
        <button
          className="btn btn-outline-primary"
          onClick={() => navigate("/sysadmin/sim-farm/devices")}
        >
          Manage Devices
        </button>
      </div>

      {/* Health Checks — all 5 checks shown, each with its own table */}
      {data.healthAlerts.map((alert, i) => (
        <div
          key={i}
          className={`card mb-3 border-start border-4 ${severityBorderClass(alert.severity)}`}
        >
          <div className="card-header py-2 d-flex justify-content-between align-items-center">
            <div>
              <strong className={severityTextClass(alert.severity)}>
                {alert.title}
              </strong>
              <span className="text-muted ms-2">{alert.description}</span>
            </div>
            <span
              className={`badge ${alert.count > 0 ? "bg-danger" : "bg-success"}`}
            >
              {alert.count > 0 ? alert.count : "OK"}
            </span>
          </div>
          {alert.devices.length > 0 && (
            <div className="card-body p-0">
              <table
                className="table table-sm table-striped mb-0"
                style={{ fontSize: "0.85rem" }}
              >
                <thead>
                  <tr>
                    {/* Check-specific columns */}
                    {alert.type === "missingCalls" && (
                      <>
                        <th>Phone</th>
                        <th>Carrier</th>
                        <th>Location</th>
                        <th>Last Screenshot</th>
                        <th>Missed Calls</th>
                      </>
                    )}
                    {alert.type === "idle" && (
                      <>
                        <th>Phone</th>
                        <th>Carrier</th>
                        <th>Location</th>
                        <th>Last Screenshot</th>
                        <th>Last Dial</th>
                        <th>Missed Calls</th>
                      </>
                    )}
                    {alert.type === "excludedScreenshots" && (
                      <>
                        <th>Phone</th>
                        <th>Carrier</th>
                        <th>Location</th>
                        <th>Excluded/Hr</th>
                      </>
                    )}
                    {alert.type === "lowSpamDetection" && (
                      <>
                        <th>Phone</th>
                        <th>Carrier</th>
                        <th>Type</th>
                        <th>Location</th>
                        <th>Total Calls</th>
                        <th>Spam Flagged</th>
                        <th>Clean Flagged</th>
                        <th>Spam %</th>
                      </>
                    )}
                    {alert.type === "callLogScreenshots" && (
                      <>
                        <th>Phone</th>
                        <th>Carrier</th>
                        <th>Location</th>
                        <th>Call-Log Screenshots</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {alert.devices.map((d) => (
                    <tr key={d.id}>
                      {alert.type === "missingCalls" && (
                        <>
                          <td>{formatPhone(d.phoneNumber)}</td>
                          <td>{d.carrier}</td>
                          <td>{d.location}</td>
                          <td>{formatDate(d.lastCalled)}</td>
                          <td>{d.missedCalls}</td>
                        </>
                      )}
                      {alert.type === "idle" && (
                        <>
                          <td>{formatPhone(d.phoneNumber)}</td>
                          <td>{d.carrier}</td>
                          <td>{d.location}</td>
                          <td>{formatDate(d.lastCalled)}</td>
                          <td>{formatDate(d.lastPlacedCallToDevice)}</td>
                          <td>{d.missedCalls ?? 0}</td>
                        </>
                      )}
                      {alert.type === "excludedScreenshots" && (
                        <>
                          <td>{formatPhone(d.phoneNumber)}</td>
                          <td>{d.carrier}</td>
                          <td>{d.location}</td>
                          <td>{d.blackScreenshotsInLastHour}</td>
                        </>
                      )}
                      {alert.type === "lowSpamDetection" && (
                        <>
                          <td>{formatPhone(d.phoneNumber)}</td>
                          <td>{d.carrier}</td>
                          <td>{d.deviceType}</td>
                          <td>{d.location}</td>
                          <td>{d.totalCalls}</td>
                          <td>{d.flaggedAsSpam}</td>
                          <td>{d.flaggedAsClean}</td>
                          <td>{d.spamPercentage != null ? `${d.spamPercentage.toFixed(1)}%` : "--"}</td>
                        </>
                      )}
                      {alert.type === "callLogScreenshots" && (
                        <>
                          <td>{formatPhone(d.phoneNumber)}</td>
                          <td>{d.carrier}</td>
                          <td>{d.location}</td>
                          <td>{d.callLogScreenshots}</td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}

      {/* Deep checks — lazy loaded */}
      {deepLoading && (
        <div className="card mb-3 border-start border-4 border-secondary">
          <div className="card-header py-2">
            <span className="text-muted">Loading deep checks (spam detection, call-log analysis)...</span>
          </div>
        </div>
      )}
      {deepAlerts.map((alert, i) => (
        <div key={`deep-${i}`} className={`card mb-3 border-start border-4 ${severityBorderClass(alert.severity)}`}>
          <div className="card-header py-2 d-flex justify-content-between align-items-center">
            <div>
              <strong className={severityTextClass(alert.severity)}>{alert.title}</strong>
              <span className="text-muted ms-2">{alert.description}</span>
            </div>
            <span className={`badge ${alert.count > 0 ? "bg-danger" : "bg-success"}`}>
              {alert.count > 0 ? alert.count : "OK"}
            </span>
          </div>
          {alert.devices.length > 0 && (
            <div className="card-body p-0">
              <table className="table table-sm table-striped mb-0" style={{ fontSize: "0.85rem" }}>
                <thead>
                  <tr>
                    {alert.type === "lowSpamDetection" && (
                      <><th>Phone</th><th>Carrier</th><th>Type</th><th>Location</th><th>Total Calls</th><th>Spam Flagged</th><th>Clean Flagged</th><th>Spam %</th></>
                    )}
                    {alert.type === "callLogScreenshots" && (
                      <><th>Phone</th><th>Carrier</th><th>Location</th><th>Call-Log Screenshots</th></>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {alert.devices.map((d) => (
                    <tr key={d.id}>
                      {alert.type === "lowSpamDetection" && (
                        <><td>{formatPhone(d.phoneNumber)}</td><td>{d.carrier}</td><td>{d.deviceType}</td><td>{d.location}</td><td>{d.totalCalls}</td><td>{d.flaggedAsSpam}</td><td>{d.flaggedAsClean}</td><td>{d.spamPercentage != null ? `${d.spamPercentage.toFixed(1)}%` : "--"}</td></>
                      )}
                      {alert.type === "callLogScreenshots" && (
                        <><td>{formatPhone(d.phoneNumber)}</td><td>{d.carrier}</td><td>{d.location}</td><td>{d.callLogScreenshots}</td></>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}

      {/* Carrier Breakdown — Physical Handsets Only */}
      <h5 className="mb-3">Physical Handsets by Carrier</h5>
      <div className="row g-3 mb-4">
        {data.carrierSummaries.map((c) => (
          <div key={c.carrier} className="col">
            <div className="card h-100">
              <div
                className="card-header text-white text-center"
                style={{
                  backgroundColor: CARRIER_COLORS[c.carrier] || "#6c757d",
                }}
              >
                <strong>{c.carrier}</strong>
              </div>
              <div className="card-body">
                <div className="row text-center g-2">
                  <div className="col-4">
                    <h3 className="text-success mb-0">{c.active}</h3>
                    <small className="text-muted">Active</small>
                  </div>
                  <div className="col-4">
                    <h3
                      className={`mb-0 ${c.warmupReserved ? "text-info" : "text-muted"}`}
                    >
                      {c.warmupReserved ?? 0}
                    </h3>
                    <small className="text-muted">Warmup</small>
                  </div>
                  <div className="col-4">
                    <h3 className="mb-0">{c.total}</h3>
                    <small className="text-muted">Total</small>
                  </div>
                  <div className="col-6 mt-3">
                    <div className="fw-bold">{c.queueDepth}</div>
                    <small className="text-muted">Queue</small>
                  </div>
                  <div className="col-6 mt-3">
                    <div className="fw-bold">{c.highPriorityQueueDepth}</div>
                    <small className="text-muted">High Priority</small>
                  </div>
                  <div className="col-6">
                    <div className="fw-bold">{c.callsInLastHour}</div>
                    <small className="text-muted">Calls / hr</small>
                  </div>
                  <div className="col-6">
                    <div className="fw-bold">{c.connectedInLastHour}</div>
                    <small className="text-muted">Connected / hr</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Queue Depth Chart */}
      <h5 className="mb-3">Queue Depth by Carrier</h5>
      <div className="card mb-4">
        <div className="card-body d-flex justify-content-center">
          <BarChart width={600} height={300} data={queueChartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="carrier" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="Queue Depth" fill="#0d6efd" />
            <Bar dataKey="High Priority" fill="#dc3545" />
          </BarChart>
        </div>
      </div>
    </div>
  );
}
